import { useState, useRef } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  Chip,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  FileUpload as FileIcon,
  ExpandLess,
  ExpandMore,
  Close as CloseIcon,
  Description as DescriptionIcon,
} from "@mui/icons-material";
import { BrokerProvider } from "../../../data/brokers";
import { useUploadTradingCsv } from "../useUploadTradingCsv";

interface Trading212CsvUploaderProps {
  broker: BrokerProvider;
  onSuccess?: (data: any) => void;
  onFilesChange?: (files: File[]) => void;
  hideNavigation?: boolean;
  embedded?: boolean;
}

const Trading212CsvUploader = ({
  broker,
  onSuccess,
  onFilesChange,
  hideNavigation = false,
  embedded = false
}: Trading212CsvUploaderProps) => {
  const [showInstructions, setShowInstructions] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastUploadResult, setLastUploadResult] = useState<any>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadTradingCsv();

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragActive(true);
    } else if (e.type === "dragleave") {
      setIsDragActive(false);
    }
  };

  const addFiles = (newFiles: File[]) => {
    setSelectedFiles(prevFiles => {
      // Create a set of existing file signatures (name + size) to detect duplicates
      const existingSignatures = new Set(
        prevFiles.map(f => `${f.name}-${f.size}`)
      );

      // Filter out duplicates
      const uniqueNewFiles = newFiles.filter(file => {
        const signature = `${file.name}-${file.size}`;
        return !existingSignatures.has(signature);
      });

      if (uniqueNewFiles.length < newFiles.length) {
        const duplicateCount = newFiles.length - uniqueNewFiles.length;
        setErrorMessage(`${duplicateCount} duplicate file(s) were skipped`);
        setShowErrorSnackbar(true);
      }

      const updatedFiles = [...prevFiles, ...uniqueNewFiles];

      // Notify parent component of file changes
      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }

      return updatedFiles;
    });
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      const csvFiles = files.filter(file => file.type === "text/csv" || file.name.endsWith(".csv"));

      if (csvFiles.length === 0) {
        setErrorMessage("Please select CSV files only");
        setShowErrorSnackbar(true);
        return;
      }

      if (csvFiles.length !== files.length) {
        setErrorMessage(`${files.length - csvFiles.length} non-CSV file(s) were ignored`);
        setShowErrorSnackbar(true);
      }

      addFiles(csvFiles);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      addFiles(files);
      // Reset input so same file can be selected again if removed and re-added
      e.target.value = '';
    }
  };

  const removeFile = (indexToRemove: number) => {
    setSelectedFiles(prevFiles => {
      const updatedFiles = prevFiles.filter((_, index) => index !== indexToRemove);

      // Notify parent component
      if (onFilesChange) {
        onFilesChange(updatedFiles);
      }

      return updatedFiles;
    });
  };

  const clearAllFiles = () => {
    setSelectedFiles([]);
    if (onFilesChange) {
      onFilesChange([]);
    }
  };

  const handleUpload = async (file: File) => {
    setUploadProgress(0);
    
    // Simulate progress updates
    const progressInterval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 20;
      });
    }, 300);

    try {
      const result = await uploadMutation.mutateAsync(file);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setLastUploadResult(result);
      setShowSuccessSnackbar(true);
      
      // Call onSuccess if provided (for wizard flow)
      if (onSuccess) {
        onSuccess({
          file,
          result,
        });
      }
      
      // Reset progress after success message
      setTimeout(() => setUploadProgress(0), 3000);
      
    } catch (error: any) {
      clearInterval(progressInterval);
      setUploadProgress(0);
      setErrorMessage(error.error || "Upload failed. Please check your file and try again.");
      setShowErrorSnackbar(true);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <Box>
      {/* Upload Area */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Box
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            sx={{
              border: `2px dashed ${isDragActive ? 'primary.main' : 'grey.300'}`,
              borderRadius: 2,
              p: 6,
              textAlign: 'center',
              cursor: 'pointer',
              backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              multiple
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            
            {selectedFiles.length > 0 ? (
              <Box>
                <FileIcon sx={{ fontSize: 48, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selected
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Click or drag to add more files
                </Typography>
                <Chip
                  label={`Total: ${formatFileSize(selectedFiles.reduce((sum, f) => sum + f.size, 0))}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Box>
            ) : (
              <Box>
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {isDragActive ? 'Drop your CSV files here' : 'Drag & drop your Trading212 CSV files'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  or click to browse and select files
                </Typography>
                <Chip
                  label="CSV files only • Max 50MB per file • Multiple files supported"
                  size="small"
                  variant="outlined"
                />
              </Box>
            )}
          </Box>

          {/* File List */}
          {selectedFiles.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Selected Files ({selectedFiles.length})
                </Typography>
                {selectedFiles.length > 1 && (
                  <Button
                    size="small"
                    onClick={clearAllFiles}
                    sx={{ color: 'text.secondary' }}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              <List sx={{ p: 0 }}>
                {selectedFiles.map((file, index) => (
                  <ListItem
                    key={`${file.name}-${file.size}-${index}`}
                    sx={{
                      border: theme => theme.palette.mode === 'dark'
                        ? '1px solid rgba(255, 255, 255, 0.1)'
                        : '1px solid rgba(0, 0, 0, 0.1)',
                      borderRadius: 2,
                      mb: 1,
                      backgroundColor: theme => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : 'rgba(0, 0, 0, 0.01)',
                      '&:hover': {
                        backgroundColor: theme => theme.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.04)'
                          : 'rgba(0, 0, 0, 0.02)',
                      },
                    }}
                    secondaryAction={
                      <Button
                        size="small"
                        onClick={() => removeFile(index)}
                        sx={{
                          minWidth: 'auto',
                          p: 1,
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.dark',
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </Button>
                    }
                  >
                    <ListItemIcon>
                      <DescriptionIcon sx={{ color: 'success.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.name}
                      secondary={formatFileSize(file.size)}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {/* Upload Result */}
          {lastUploadResult && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Upload Complete!</strong><br />
                Imported {lastUploadResult.imported_count} transactions from {lastUploadResult.total_rows} rows
                {lastUploadResult.skipped_count > 0 && (
                  <> • {lastUploadResult.skipped_count} duplicates skipped</>
                )}
              </Typography>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Export Instructions */}
      <Card>
        <CardContent sx={{ p: 3 }}>
          <Button
            variant="text"
            fullWidth
            onClick={() => setShowInstructions(!showInstructions)}
            endIcon={showInstructions ? <ExpandLess /> : <ExpandMore />}
            startIcon={<InfoIcon />}
          >
            How to export from Trading212
          </Button>

          <Collapse in={showInstructions}>
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Step-by-step export guide:
              </Typography>
              
              <List dense>
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                      1.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Log into your Trading212 account"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                      2.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Navigate to History → Transactions"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                      3.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Select your desired date range (or leave blank for all history)"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                      4.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Click 'Export' and choose CSV format"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
                
                <ListItem sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <Typography variant="body2" color="primary" sx={{ fontWeight: "bold" }}>
                      5.
                    </Typography>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Download the file and upload it above"
                    primaryTypographyProps={{ variant: "body2" }}
                  />
                </ListItem>
              </List>

              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>Pro tip:</strong> Include all historical data in your first upload for 
                  complete portfolio analysis. You can always upload newer data later to update your portfolio.
                </Typography>
              </Alert>

              <Alert severity="warning" sx={{ mt: 1 }}>
                <Typography variant="body2">
                  <strong>File format:</strong> Make sure to export as CSV format, not Excel or PDF. 
                  The file should include columns like Date, Action, Symbol, Quantity, Price, etc.
                </Typography>
              </Alert>
            </Box>
          </Collapse>
        </CardContent>
      </Card>

      {/* What happens next */}
      <Box 
        sx={{ 
          mt: 3, 
          p: 3, 
          backgroundColor: "rgba(0, 0, 0, 0.04)", 
          borderRadius: 2,
          border: "1px solid rgba(0, 0, 0, 0.12)"
        }}
      >
        <Typography variant="h6" gutterBottom>
          What happens after upload?
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • Your transaction data is securely processed and validated<br />
          • Portfolio performance and analytics are calculated automatically<br />
          • You'll be able to view detailed insights and track your investments<br />
          • All data is securely stored and only accessible to your account<br />
          • Upload additional CSV files anytime to update your portfolio
        </Typography>
      </Box>

      {/* Success Snackbar */}
      <Snackbar
        open={showSuccessSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowSuccessSnackbar(false)}
      >
        <Alert 
          onClose={() => setShowSuccessSnackbar(false)} 
          severity="success"
          sx={{ width: "100%" }}
        >
          Portfolio data uploaded successfully! Your transactions are being processed.
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={showErrorSnackbar}
        autoHideDuration={8000}
        onClose={() => setShowErrorSnackbar(false)}
      >
        <Alert 
          onClose={() => setShowErrorSnackbar(false)} 
          severity="error"
          sx={{ width: "100%" }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Trading212CsvUploader;